// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
// import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector"; // Starter Custom Code
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import List from "@insite/mobius/Icons/List";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Heading, { HeadingPresentationProps } from "@store/Library/Components/Toolkit/Heading";
import Upload from "@store/Library/Components/Toolkit/Icons/Upload";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import ProductSelector, { ProductSelectorStyles } from "@store/Overrides/Components/ProductSelector";
/* Starter Custom Code - End */

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        canOrderUpload: settingsCollection.orderSettings.canOrderUpload,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        orderUploadPageNavLink: getPageLinkByPageType(state, "OrderUploadPage"),
        quickOrderPageNavLink: getPageLinkByPageType(state, "QuickOrderPage"),
        allowQuickOrder: settingsCollection.orderSettings.allowQuickOrder,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

export type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface QuickOrderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    linksGridItem?: GridItemProps;
    orderUploadLink?: LinkPresentationProps;
    orderMultipleItemsLink?: LinkPresentationProps;
    productSelector?: ProductSelectorStyles;
    // Starter Custom Code
    heading?: HeadingPresentationProps;
    selectorWrapper?: InjectableCss;
}

export const quickOrderStyles: QuickOrderStyles = {
    orderMultipleItemsLink: linkGetActionLinkProps(List),
    orderUploadLink: linkGetActionLinkProps(Upload),
    heading: {
        variant: "h4",
        forwardAs: "h2",
        extendedStyles: {
            title: {
                css: css`
                    ${spacingStyles(null, "0.75em")}
                    padding-bottom: 0.6em;
                    border-bottom: 1px solid ${getColor("common.border")};
                `,
            },
        },
    },
};

export const QuickOrder = ({
    canOrderUpload,
    showAddToCartConfirmationDialog,
    orderUploadPageNavLink,
    quickOrderPageNavLink,
    toaster,
    addToCart,
    allowQuickOrder,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>("");
    const trackAddToCart = ga.use.trackAddToCart(); // Starter Custom Code
    const styles = useMergeStyles("quickOrder", quickOrderStyles); // Starter Custom Code

    if (!allowQuickOrder) {
        return null;
    }

    /* Starter Moved - useMergeStyles - up */

    const addProductToCart = async (productInfo: ProductInfo, product: ProductModel) => {
        if (
            !product.quoteRequired &&
            !product.allowZeroPricing &&
            productInfo.pricing &&
            getUnitNetPrice(productInfo.pricing, 1).price === 0
        ) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const cartline = (await addToCart({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
            onError: () => {
                setErrorMessage(siteMessage("Product_NotFound"));
            },
        })) as CartLineModel;

        trackAddToCart({ items: [product] }, { productInfos: [productInfo] }); // Starter Custom Code

        if (showAddToCartConfirmationDialog) {
            toaster.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    return (
        <>
            {/* Starter Removed - Grid */}
            <Heading title={translate("Quick Order")} {...styles.heading} data-test-selector="quickOrderWidget_title">
                {canOrderUpload && (
                    <Link
                        {...styles.orderUploadLink}
                        href={orderUploadPageNavLink ? orderUploadPageNavLink.url : undefined}
                        data-test-selector="quickOrderWidget_uploadAnOrder"
                    >
                        {translate("Upload an Order")}
                    </Link>
                )}
                <Link
                    {...styles.orderMultipleItemsLink}
                    href={quickOrderPageNavLink ? quickOrderPageNavLink.url : undefined}
                    data-test-selector="quickOrderWidget_orderMultipleItems"
                >
                    {translate("Order Multiple Items")}
                </Link>
            </Heading>
            <StyledWrapper {...styles.selectorWrapper} data-test-selector="quickOrderWidget" /* Starter Custom Code */>
                <ProductSelector
                    selectButtonTitle={translate("Add to Cart")}
                    onSelectProduct={addProductToCart}
                    productIsConfigurableMessage={siteMessage("QuickOrder_CannotOrderConfigurable")}
                    productIsUnavailableMessage={siteMessage("QuickOrder_ProductIsUnavailable")}
                    customErrorMessage={errorMessage}
                    extendedStyles={styles.productSelector}
                    data-test-selector="quickOrderWidget_addToCart"
                    // Starter Custom Code
                    showSku
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(QuickOrder)),
    definition: {
        group: "Basic",
        icon: "cart-shopping-fast",
    },
};

export default widgetModule;
